<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import DeleteConfirmationModal from "@/components/modal/DeleteConfirmationModal.vue";
import AddBucketOptionModal from "@/components/bucket/options/AddBucketOptionModal.vue";
import EditBucketOptionModal from "@/components/bucket/options/EditBucketOptionModal.vue";
import BucketListSection from "@/components/dashboard/BucketListSection.vue";
import OnboardingBanner from "@/components/onboarding/OnboardingBanner.vue";
import DateFilterControls from "@/components/dashboard/DateFilterControls.vue";
import DashboardActivitiesWidget from "@/components/dashboard/DashboardActivitiesWidget.vue";
import DashboardMeetingsWidget from "@/components/dashboard/DashboardMeetingsWidget.vue";
import DashboardOpportunitiesWidget from "@/components/dashboard/DashboardOpportunitiesWidget.vue";
import OwnerFilterPicker from "@/components/common/OwnerFilterPicker.vue";

import { useDashboardStats } from "@/composables/dashboard/useDashboardStats";
import { useUsers } from "@/composables/organization/useUsers";
import { useOrganizationSetting } from "@/composables/organization/useOrganizationSetting";
import { useDeleteBucketOption } from "@/composables/bucket/useBucketOption";
import { useToast } from "@/composables/ui/useToast";
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import { GET_TRIAL_STATUS } from "@/store/action-types";
import { SUBSCRIPTION_WALL_ROUTE, ONBOARDING_ROUTE } from "@/router/routes";
import { usePermissions } from "@/composables/usePermissions";
import {
  isOnboardingChecklistDismissed,
  isOnboardingChecklistCompleted,
} from "@/utils/onboardingChecklistStorage";

defineOptions({
  name: "Dashboard",
});

const route = useRoute();
const router = useRouter();
const store = useStore();

const account = computed(() => store.getters.account.data);
const { canManageContacts } = usePermissions();
const $toast = useToast();

// Local state
const showAddBucketOptionModal = ref(false);
const showEditBucketOptionModal = ref(false);
const showDeleteConfirmationModal = ref(false);
const selectedBucketOption = ref(null);

// Initialize date range to current week
const now = new Date();
const dayOfWeek = now.getDay();
const diffToMonday = (dayOfWeek + 6) % 7;
const monday = new Date(now);
monday.setDate(now.getDate() - diffToMonday);
monday.setHours(0, 0, 0, 0);
const sunday = new Date(monday);
sunday.setDate(monday.getDate() + 6);
sunday.setHours(23, 59, 59, 999);
const dateRange = ref([monday, sunday]);
const selectedFilter = ref("thisWeek");

// Computed params for API calls
const dashboardParams = computed(() => {
  if (!account.value?.organizationId) return null;

  const now = new Date();
  const dayOfWeek = now.getDay();
  const diffToMonday = (dayOfWeek + 6) % 7;
  const monday = new Date(now);
  monday.setDate(now.getDate() - diffToMonday);
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  const todayStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const todayEndDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

  const params = {
    todayStartDate,
    todayEndDate,
    offset: new Date().getTimezoneOffset(),
    startDate: dateRange.value?.[0]?.toISOString() ?? monday.toISOString(),
    endDate: dateRange.value?.[1]?.toISOString() ?? sunday.toISOString(),
    organizationId: account.value.organizationId,
  };

  // Handle bucket owner filter
  if (canManageContacts.value) {
    if (route.query.bucketOwner && route.query.bucketOwner !== "All") {
      params.bucketOwnerId = route.query.bucketOwner;
    }
  } else {
    params.bucketOwnerId = account.value.id;
  }

  // Handle activity owner filter
  if (canManageContacts.value) {
    if (route.query.activityOwner && route.query.activityOwner !== "All") {
      params.activityOwnerId = route.query.activityOwner;
    }
  } else {
    params.activityOwnerId = account.value.id;
  }

  return params;
});

const orgSettingsParams = computed(() => {
  if (!account.value?.organizationId) return null;
  return { organizationId: account.value.organizationId };
});

// Dashboard stats query
const {
  data: dashboardStatsData,
  isLoading: isLoadingDashboardStats,
  isFetching: isFetchingDashboardStats,
  refetch: refetchDashboardStats,
} = useDashboardStats(dashboardParams);

// Users query
const { data: usersData } = useUsers(orgSettingsParams);

// Organization setting query
const { data: organizationSettingData } = useOrganizationSetting(orgSettingsParams);

// Delete bucket option mutation
const { mutateAsync: deleteBucketOptionMutation } = useDeleteBucketOption();

const initializeDefaultFilters = () => {
  if (
    !usersData.value ||
    !Array.isArray(usersData.value) ||
    usersData.value.length === 0 ||
    !account.value
  ) {
    return;
  }

  const needsUpdate = {};
  let shouldUpdate = false;

  const orgOwner = usersData.value.find((user) => user.role === "owner");

  if (!route.query.bucketOwner && orgOwner) {
    needsUpdate.bucketOwner = orgOwner.id;
    shouldUpdate = true;
  }

  if (!route.query.activityOwner) {
    needsUpdate.activityOwner = "All";
    shouldUpdate = true;
  }

  if (shouldUpdate) {
    const { owner, ...restQuery } = route.query;
    const cleanQuery = {
      ...(restQuery.bucketOwner && { bucketOwner: restQuery.bucketOwner }),
      ...(restQuery.activityOwner && { activityOwner: restQuery.activityOwner }),
      ...needsUpdate,
    };
    router.replace({
      query: cleanQuery,
    });
  }
};

const cleanupOwnerParam = () => {
  if (route.query.owner) {
    const { owner, ...restQuery } = route.query;
    router.replace({
      query: {
        ...(restQuery.bucketOwner && { bucketOwner: restQuery.bucketOwner }),
        ...(restQuery.activityOwner && { activityOwner: restQuery.activityOwner }),
      },
    });
  }
};

const changingBucketFilter = ref(false);
const changingActivityFilter = ref(false);
const hasInitialData = ref(false);

const isLoadingBucketStats = computed(() => {
  const isInitialLoad = isLoadingDashboardStats.value && !hasInitialData.value;
  const isBucketFilterChanging = changingBucketFilter.value && isFetchingDashboardStats.value;

  return isInitialLoad || isBucketFilterChanging;
});

const isLoadingActivityStats = computed(() => {
  const isInitialLoad = isLoadingDashboardStats.value && !hasInitialData.value;
  const isActivityFilterChanging = changingActivityFilter.value && isFetchingDashboardStats.value;

  return isInitialLoad || isActivityFilterChanging;
});

watch(
  () => route.query.bucketOwner,
  (newValue, oldValue) => {
    if (oldValue !== undefined && oldValue !== newValue) {
      changingBucketFilter.value = true;
      changingActivityFilter.value = false;
    }
  },
  { immediate: true }
);

watch(
  () => [route.query.activityOwner, dateRange.value],
  (newValue, oldValue) => {
    const [newActivityOwner, newDateRange] = newValue || [undefined, undefined];
    const [oldActivityOwner, oldDateRange] = oldValue || [undefined, undefined];

    const dateRangeKey = newDateRange
      ? `${newDateRange[0]?.toISOString()}-${newDateRange[1]?.toISOString()}`
      : null;
    const oldDateRangeKey = oldDateRange
      ? `${oldDateRange[0]?.toISOString()}-${oldDateRange[1]?.toISOString()}`
      : null;

    if (
      oldActivityOwner !== undefined &&
      (oldActivityOwner !== newActivityOwner || oldDateRangeKey !== dateRangeKey)
    ) {
      changingActivityFilter.value = true;
      changingBucketFilter.value = false;
    }
  },
  { immediate: true }
);

watch(
  dashboardStatsData,
  (data) => {
    if (data && !hasInitialData.value) {
      hasInitialData.value = true;
    }
  },
  { immediate: true }
);

watch(isFetchingDashboardStats, (isFetching) => {
  if (!isFetching) {
    if (changingBucketFilter.value) {
      changingBucketFilter.value = false;
    }
    if (changingActivityFilter.value) {
      changingActivityFilter.value = false;
    }
  }
});

const sortedBucketStats = computed(() => {
  if (!dashboardStatsData.value?.bucketStatsByOption) return [];
  if (!organizationSettingData.value?.bucketOrder) {
    return dashboardStatsData.value.bucketStatsByOption;
  }

  const bucketOrder = organizationSettingData.value.bucketOrder;
  return [...dashboardStatsData.value.bucketStatsByOption].sort((a, b) => {
    return bucketOrder.indexOf(a.bucketOption.slug) - bucketOrder.indexOf(b.bucketOption.slug);
  });
});

const selectBucketToEdit = (bucketOption) => {
  selectedBucketOption.value = bucketOption;
  showEditBucketOptionModal.value = true;
};

const toggleDeleteConfirmationModal = (bucketOption) => {
  selectedBucketOption.value = bucketOption;
  showDeleteConfirmationModal.value = true;
};

const handleDeleteBucketOption = async () => {
  try {
    await deleteBucketOptionMutation(selectedBucketOption.value.id);
    selectedBucketOption.value = null;
    return true;
  } catch (error) {
    console.error("Failed to delete bucket option:", error);
    $toast.error("Failed to delete bucket.");
    return false;
  }
};

const handleDateRangeUpdate = (newDateRange) => {
  if (
    !newDateRange ||
    !dateRange.value ||
    newDateRange[0]?.getTime() !== dateRange.value[0]?.getTime() ||
    newDateRange[1]?.getTime() !== dateRange.value[1]?.getTime()
  ) {
    // Ensure dates are valid Date objects
    if (newDateRange && newDateRange.length === 2) {
      const startDate =
        newDateRange[0] instanceof Date ? newDateRange[0] : new Date(newDateRange[0]);
      const endDate = newDateRange[1] instanceof Date ? newDateRange[1] : new Date(newDateRange[1]);

      dateRange.value = [startDate, endDate];
    } else {
      dateRange.value = newDateRange;
    }
    selectedFilter.value = null;
  }
};

const filterByDate = (filter) => {
  const now = new Date();
  let startDate, endDate;

  switch (filter) {
    case "today":
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date();
      endDate.setHours(23, 59, 59, 999);
      break;
    case "yesterday":
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999);
      break;
    case "thisWeek":
      const weekNow = new Date();
      const dayOfWeek = weekNow.getDay();
      const diffToMonday = (dayOfWeek + 6) % 7;
      startDate = new Date(weekNow);
      startDate.setDate(weekNow.getDate() - diffToMonday);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 59, 59, 999);
      break;
    case "pastWeek":
      const pastWeekNow = new Date();
      const pastWeekDayOfWeek = pastWeekNow.getDay();
      const pastWeekDiffToMonday = (pastWeekDayOfWeek + 6) % 7;
      endDate = new Date(pastWeekNow);
      endDate.setDate(pastWeekNow.getDate() - pastWeekDiffToMonday - 1);
      endDate.setHours(23, 59, 59, 999);
      startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 6);
      startDate.setHours(0, 0, 0, 0);
      break;
    case "thisMonth":
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      break;
    case "pastMonth":
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      break;
    case "thisQuarter": {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      startDate = new Date(now.getFullYear(), currentQuarter * 3, 1);
      endDate = new Date(now.getFullYear(), currentQuarter * 3 + 3, 0, 23, 59, 59, 999);
      break;
    }
    case "pastQuarter": {
      const currentQtr = Math.floor(now.getMonth() / 3);
      let pastQtr = currentQtr - 1;
      let pastQtrYear = now.getFullYear();
      if (pastQtr < 0) {
        pastQtr = 3;
        pastQtrYear -= 1;
      }
      startDate = new Date(pastQtrYear, pastQtr * 3, 1);
      endDate = new Date(pastQtrYear, pastQtr * 3 + 3, 0, 23, 59, 59, 999);
      break;
    }
    case "thisYear":
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
      break;
    case "pastYear":
      startDate = new Date(now.getFullYear() - 1, 0, 1);
      endDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
      break;
  }

  dateRange.value = startDate && endDate ? [startDate, endDate] : [];
  selectedFilter.value = filter;
};

// Shared filters for the overview widgets (date range + activity owner)
const activityOwnerKey = computed(() => {
  const q = route.query.activityOwner;
  if (!q || q === "All") return "all";
  return q;
});

const widgetOwnerId = computed(() => dashboardParams.value?.activityOwnerId ?? null);
const widgetStartDate = computed(() => dashboardParams.value?.startDate ?? null);
const widgetEndDate = computed(() => dashboardParams.value?.endDate ?? null);

const selectedPeriodLabel = computed(() => {
  const [start, end] = dateRange.value || [];
  if (!start || !end) return "";
  const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const sameYear = start.getFullYear() === end.getFullYear();
  const year = sameYear ? `, ${end.getFullYear()}` : "";
  return `${fmt(start)} – ${fmt(end)}${year}`;
});

const onActivityOwnerSelect = (payload) => {
  handleOwnerSelected({ user: payload === "all" ? "All" : payload, type: "activity" });
};

const handleOwnerSelected = ({ user, type }) => {
  const { owner, ...restQuery } = route.query;
  const cleanQuery = {
    ...(restQuery.bucketOwner && { bucketOwner: restQuery.bucketOwner }),
    ...(restQuery.activityOwner && { activityOwner: restQuery.activityOwner }),
  };

  if (type === "bucket") {
    router.replace({
      query: {
        ...cleanQuery,
        bucketOwner: user == "All" ? "All" : user.id,
      },
    });
  } else if (type === "activity") {
    router.replace({
      query: {
        ...cleanQuery,
        activityOwner: user == "All" ? "All" : user.id,
      },
    });
  }
};

watch(
  usersData,
  () => {
    if (usersData.value) {
      initializeDefaultFilters();
    }
  },
  { immediate: true }
);

onMounted(async () => {
  cleanupOwnerParam();

  if (usersData.value) {
    initializeDefaultFilters();
  }

  // Redirect to subscription wall if user hasn't chosen a plan yet
  await store.dispatch(GET_TRIAL_STATUS);
  if (!store.getters.hasCompletedOnboarding) {
    router.push({ name: SUBSCRIPTION_WALL_ROUTE });
    return;
  }

  // Send the user through the getting-started checklist on login/signup until all steps
  // are done. "Completed" is permanent (set once GettingStarted verifies every step is
  // done) so finished users never re-enter this flow. "Dismissed" only defers it for the
  // current session and is cleared on the next login for users who haven't finished yet.
  if (
    !isOnboardingChecklistCompleted(account.value?.id) &&
    !isOnboardingChecklistDismissed(account.value?.id)
  ) {
    router.push({ name: ONBOARDING_ROUTE });
  }
});
</script>

<template>
  <DefaultLayout>
    <div class="dashboard-body">
      <OnboardingBanner />
      <!-- Modals -->
      <AddBucketOptionModal
        v-if="showAddBucketOptionModal"
        @close-add-bucket-option-modal="showAddBucketOptionModal = false"
        @refresh-bucket-options="refetchDashboardStats"
      />
      <div class="content-section">
        <EditBucketOptionModal
          v-if="showEditBucketOptionModal"
          :selectedBucketOption="selectedBucketOption"
          @closeEditBucketOptionModal="showEditBucketOptionModal = false"
          @refreshBucketOptions="refetchDashboardStats"
        />
        <DeleteConfirmationModal
          v-if="showDeleteConfirmationModal"
          :deleteFunction="handleDeleteBucketOption"
          entity="Bucket"
          @closeDeleteConfirmationModal="showDeleteConfirmationModal = false"
          @refreshRecords="refetchDashboardStats"
        />

        <BucketListSection
          :account="account"
          @add-bucket="showAddBucketOptionModal = true"
          @edit-bucket="selectBucketToEdit"
          @delete-bucket="toggleDeleteConfirmationModal"
          @owner-selected="handleOwnerSelected"
        />

        <section class="overview-section">
          <div class="overview-header">
            <div class="overview-title-group">
              <h6 class="overview-title">Overview</h6>
              <span v-if="selectedPeriodLabel" class="overview-period">
                {{ selectedPeriodLabel }}
              </span>
              <OwnerFilterPicker
                :model-value="activityOwnerKey"
                :users="usersData"
                @select="onActivityOwnerSelect"
              />
            </div>
            <DateFilterControls
              :dateRange="dateRange"
              @update:dateRange="handleDateRangeUpdate"
              @filterByDate="filterByDate"
            />
          </div>

          <div class="overview-grid">
            <DashboardMeetingsWidget
              :organization-id="account?.organizationId"
              :start-date="widgetStartDate"
              :end-date="widgetEndDate"
              :contact-owner-id="widgetOwnerId ?? undefined"
            />
            <DashboardOpportunitiesWidget
              :organization-id="account?.organizationId"
              :start-date="widgetStartDate"
              :end-date="widgetEndDate"
              :owner-id="widgetOwnerId"
            />
            <div class="overview-grid-full">
              <DashboardActivitiesWidget
                :daily-counts="dashboardStatsData?.dailyTaskTypeCounts"
                :daily-call-durations="dashboardStatsData?.dailyCallDurations"
                :is-loading="isLoadingActivityStats"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  </DefaultLayout>
</template>

<style scoped>
.dashboard-body {
  width: 100%;
}

.content-section {
  padding: 16px 20px;
  max-width: none;
  width: 100%;
}

.overview-section {
  margin-top: 28px;
  width: 100%;
}

.overview-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-bottom: 16px;
}

.overview-title-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.overview-title {
  margin: 0;
  font-weight: 600;
  font-size: 1.25rem;
  color: #111827;
}

.overview-period {
  font-size: 0.8rem;
  font-weight: 500;
  color: #4b5563;
  background: #f3f4f6;
  padding: 4px 10px;
  border-radius: 999px;
}

.overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 20px;
  align-items: stretch;
}

.overview-grid-full {
  grid-column: 1 / -1;
}

@media (max-width: 1100px) {
  .content-section {
    padding: 12px 16px;
  }

  .overview-section {
    margin-top: 20px;
  }

  .overview-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
}
</style>
